/**
 * Per-chapter accuracy, auto-graded vs self-graded (brief §teacher: "auto vs
 * self series clearly labelled 'automatiskt rättat' / 'självrättat'").
 * Grading provenance is never mixed into one number (spec §4.1) — always two
 * series, never a blended "accuracy".
 */
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { useTheme } from "@client/app/theme";
import { useLang, useT } from "@client/i18n";
import { formatPercent } from "@client/lib/format";
import type { ChapterAccuracyRow } from "./types";

interface ChartRow {
  chapter: number;
  auto: number;
  self: number;
  attempts: number;
}

function TooltipContent({
  active,
  payload,
  lang,
}: {
  active?: boolean;
  payload?: readonly { payload?: ChartRow }[];
  lang: "sv" | "en";
}) {
  const t = useT();
  const row = active ? payload?.[0]?.payload : undefined;
  if (!row) return null;
  return (
    <div className="surface px-3 py-2 text-xs shadow-card">
      <p className="mb-1 font-medium text-ink-800 dark:text-ink-100">
        {t("teacher.chapterLabel")} {row.chapter}
      </p>
      <p className="text-ink-600 dark:text-ink-400">
        {t("teacher.overview.chart.auto")}: {formatPercent(row.auto, lang)}
      </p>
      <p className="text-ink-600 dark:text-ink-400">
        {t("teacher.overview.chart.self")}: {formatPercent(row.self, lang)}
      </p>
    </div>
  );
}

export function ChapterAccuracyChart({ chapters }: { chapters: readonly ChapterAccuracyRow[] }) {
  const t = useT();
  const { lang } = useLang();
  const { theme } = useTheme();
  const dark = theme === "dark";

  if (chapters.length === 0 || chapters.every((c) => c.attempts === 0)) {
    return (
      <p className="text-sm text-ink-600 dark:text-ink-400">{t("teacher.overview.chart.empty")}</p>
    );
  }

  const autoColor = dark ? "#818cf8" : "#4f46e5"; // accent-400 / accent-600
  const selfColor = dark ? "#facc15" : "#ca8a04"; // amber-400 / amber-600

  const data: ChartRow[] = chapters.map((c) => ({
    chapter: c.chapter,
    auto: c.autoAccuracy,
    self: c.selfAccuracy,
    attempts: c.attempts,
  }));

  return (
    <div>
      {/*
       * A plain HTML legend, not recharts' own <Legend> — that one lives
       * inside <ResponsiveContainer>, which needs a real, non-zero measured
       * size (via ResizeObserver) before it renders anything at all, so its
       * labels are invisible whenever the container hasn't been laid out yet
       * (every component test in jsdom). This legend is always in the DOM,
       * which is also what makes the auto/self labelling reliably visible to
       * a screen reader rather than only to sighted users hovering the chart.
       */}
      <div className="mb-2 flex flex-wrap gap-4 text-xs text-ink-600 dark:text-ink-400">
        <span className="flex items-center gap-1.5">
          <span
            className="h-2.5 w-2.5 rounded-sm"
            style={{ backgroundColor: autoColor }}
            aria-hidden="true"
          />
          {t("teacher.overview.chart.auto")}
        </span>
        <span className="flex items-center gap-1.5">
          <span
            className="h-2.5 w-2.5 rounded-sm"
            style={{ backgroundColor: selfColor }}
            aria-hidden="true"
          />
          {t("teacher.overview.chart.self")}
        </span>
      </div>
      <div role="img" aria-label={t("teacher.overview.chart.title")} className="h-64 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid
              strokeDasharray="3 3"
              stroke={dark ? "#1e293b" : "#e2e8f0"}
              vertical={false}
            />
            <XAxis
              dataKey="chapter"
              tickFormatter={(v: number) => String(v)}
              tick={{ fontSize: 11, fill: dark ? "#94a3b8" : "#64748b" }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              domain={[0, 1]}
              tickFormatter={(v: number) => formatPercent(v, lang)}
              tick={{ fontSize: 11, fill: dark ? "#94a3b8" : "#64748b" }}
              width={44}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip
              content={(props) => <TooltipContent {...props} lang={lang} />}
              cursor={{ fill: dark ? "#1e293b" : "#f1f5f9" }}
            />
            {/*
             * `isAnimationActive={false}`: recharts' default grow-in
             * animation raced its own `ResponsiveContainer` sizing on
             * mount at a narrow (mobile) initial viewport — the bars would
             * sometimes stay at their pre-animation zero height, with the
             * axes/legend/gridlines drawn but no visible bars at all, and
             * nothing ever repainted them. The animation is a pure cosmetic
             * flourish; static, immediately-correct bars are worth more
             * here than a 300ms grow-in, especially for a data reading a
             * teacher needs right away.
             */}
            <Bar
              dataKey="auto"
              name="auto"
              fill={autoColor}
              radius={[2, 2, 0, 0]}
              isAnimationActive={false}
            />
            <Bar
              dataKey="self"
              name="self"
              fill={selfColor}
              radius={[2, 2, 0, 0]}
              isAnimationActive={false}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export default ChapterAccuracyChart;
